import { AlertTriangle, CheckCircle2, Circle, XCircle } from "lucide-react";
import type { FC, ReactNode } from "react";

import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

import { usePortalT } from "../i18n";
import type { UploadCheck } from "../types";

const Line: FC<{ ok: boolean | null; children: ReactNode }> = ({
  ok,
  children
}) => {
  const Icon = ok === null ? Circle : ok ? CheckCircle2 : XCircle;
  return (
    <li className="flex items-center gap-2 text-sm">
      <Icon
        className={cn(
          "size-4 shrink-0",
          ok === null && "text-muted-foreground",
          ok === true && "text-success",
          ok === false && "text-warn"
        )}
      />
      {children}
    </li>
  );
};

type Props = {
  name: string;
  /** `undefined` while the check runs. */
  check: UploadCheck | undefined;
  /** Ink signing: lead with the signature and whole-page checks. */
  signature?: boolean;
  actions?: ReactNode;
  className?: string;
};

/** What we read off an upload, so a blurry or cut-off photo gets retaken now. */
export const UploadCheckCard: FC<Props> = ({
  name,
  check,
  signature = false,
  actions,
  className
}) => {
  const { t } = usePortalT();
  const shell = (body: ReactNode, tone: "muted" | "ok" | "warn") => (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-lg border px-3.5 py-3",
        tone === "ok" && "border-success-line bg-success-soft",
        tone === "warn" && "border-warn-line bg-warn-soft",
        tone === "muted" && "border-border bg-fill-1",
        className
      )}
      role="status"
      data-testid="upload-check"
    >
      {body}
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );

  if (!check)
    return shell(
      <span className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner />
        {signature ? t("sign.checking") : t("documents.checking", { name })}
      </span>,
      "muted"
    );

  if (!check.checked)
    return shell(
      <span className="text-sm text-muted-foreground">
        {t("sign.unchecked")}
      </span>,
      "muted"
    );

  const facts = check.facts.length ? (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">
        {t("documents.weRead")}
      </span>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm">
        {check.facts.map((f) => (
          <div key={f.label} className="contents">
            <dt className="text-muted-foreground">{f.label}</dt>
            <dd className="font-medium tabular-nums">{f.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  ) : null;

  const problems = check.problems.length ? (
    <ul className="flex list-disc flex-col gap-0.5 ps-5 text-sm text-warn">
      {check.problems.map((p) => (
        <li key={p}>{p}</li>
      ))}
    </ul>
  ) : null;

  if (signature)
    return shell(
      <>
        <ul className="flex flex-col gap-1.5">
          <Line ok={check.signature_found}>
            {check.signature_found ? t("sign.found") : t("sign.notFound")}
          </Line>
          <Line ok={check.readable && check.whole_page}>
            {check.readable && check.whole_page
              ? t("sign.readable")
              : t("sign.notReadable")}
          </Line>
        </ul>
        {problems}
      </>,
      check.ok ? "ok" : "warn"
    );

  return shell(
    <>
      <span className="flex items-center gap-2 text-sm font-medium">
        {check.ok ? (
          <CheckCircle2 className="size-4 shrink-0 text-success" />
        ) : (
          <AlertTriangle className="size-4 shrink-0 text-warn" />
        )}
        {check.ok
          ? t("documents.looksGood", { name })
          : t("documents.mayBeHard", { name })}
      </span>
      {facts}
      {problems}
    </>,
    check.ok ? "ok" : "warn"
  );
};
